import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Crosshair,
  Gift,
  Info,
  Leaf,
  Package,
  Trophy,
  Users,
  Wand2,
} from 'lucide-react'
import {
  GIFTING_ACTIVATION_FEE,
  GIFTING_USAGE_DAYS,
  suggestedBonuses,
  viewsLabel,
} from '@adthlete/common/utils/dealPricing'
import { useCampaign } from '@/hooks/useCampaign'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { useCreateCampaignMutation } from '@/redux/marketerApi'
import { cn } from '@/lib/utils'
import { WizardCard, WizardFooter } from '@/components/campaign/wizard/WizardParts'
import {
  CAMPAIGN_TYPES,
  DEFAULT_BRIEF,
  PLATFORMS,
  TONES,
} from '@/components/campaign/wizard/options'

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`
const shortDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <div className="mt-0.5 text-sm font-medium text-foreground">{children}</div>
  </div>
)

const initials = (name: string) =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const Step3Review: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { campaignData, resetCampaign, goToStep } = useCampaign()
  const { basics, targeting, creative } = campaignData
  const brief = { ...DEFAULT_BRIEF, ...(creative.campaignBrief ?? {}) }
  const { marketer } = useAuth()
  const [createCampaign, { isLoading }] = useCreateCampaignMutation()
  const type = CAMPAIGN_TYPES.find((t) => t.id === basics.dealType) ?? CAMPAIGN_TYPES[0]
  const ladder: { views: number; amount: number }[] =
    basics.giftingBonuses ??
    (marketer as any)?.org?.preferences?.gifting_bonuses ??
    suggestedBonuses()
  const maxBonus = ladder.reduce((s, t) => s + t.amount, 0)
  const platforms = PLATFORMS.filter((p) => basics.socialPlatform.includes(p.id))
  const dates =
    basics.dateType === 'timeframe'
      ? basics.timeframeDays
        ? `${basics.timeframeDays} days`
        : null
      : basics.startDate
        ? [shortDate(basics.startDate), shortDate(basics.endDate)].filter(Boolean).join(' – ')
        : null
  const audience = [brief.targetAgeRange, brief.targetGender, brief.targetLocation]
    .filter(Boolean)
    .join(' · ')
  const posts = brief.postsPerAthlete ?? 1
  const contentLine =
    basics.dealType === 'appearance'
      ? 'In-person appearance'
      : `${posts} post${posts === 1 ? '' : 's'}${platforms.length ? ` (${platforms.map((p) => p.id).join(', ')})` : ''}`
  const pay =
    basics.dealType === 'gifting'
      ? `Product + bonuses up to ${money(maxBonus)}`
      : basics.payout
        ? `${money(basics.payout)} per athlete${basics.dealType === 'licensing' ? ' / month' : ''}`
        : 'Priced from each athlete’s own prices'
  const photo = brief.productPhotoUrl || (basics.logoUrl && basics.logoUrl) || null

  const create = async () => {
    try {
      const res: any = await createCampaign({ basics, targeting, creative }).unwrap()
      toast({
        title: basics.openToApplications ? 'Campaign live' : 'Campaign created',
        description: basics.openToApplications
          ? `"${basics.name}" is in the Marketplace. Athletes can apply now.`
          : `"${basics.name}" is ready. Invite athletes from the Marketplace.`,
      })
      resetCampaign()
      navigate(`/campaign/${res.data.id}`)
    } catch (err: any) {
      toast({
        title: "Couldn't create the campaign",
        description: err?.data?.error ?? err?.message ?? 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground sm:text-3xl">Review your campaign</h2>
        <p className="mt-1 text-muted-foreground">
          Make sure everything looks right before publishing. You can go back to make changes.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-4">
          <WizardCard icon={Package} title="Campaign basics" onEdit={() => goToStep(1)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Row label="Campaign name">{basics.name || '—'}</Row>
              <Row label="Type">{type.label}</Row>
              <Row label="Brand">
                <span className="flex items-center gap-2">
                  {basics.logoUrl ? (
                    <img src={basics.logoUrl} alt="" className="h-7 w-7 rounded-md object-cover" />
                  ) : null}
                  {basics.brand || '—'}
                </span>
              </Row>
              <Row label="Campaign dates">{dates ?? 'Not set'}</Row>
              <Row label="Social platforms">
                {platforms.length ? (
                  <span className="flex gap-2">
                    {platforms.map((p) => {
                      const Icon = p.icon
                      return (
                        <Icon key={p.id} className={cn('h-5 w-5', p.color)} aria-label={p.id} />
                      )
                    })}
                  </span>
                ) : (
                  'Any'
                )}
              </Row>
              <Row label="Budget">{basics.budget ? money(basics.budget) : 'Not set'}</Row>
            </div>
          </WizardCard>

          <WizardCard icon={Crosshair} title="Creative brief" onEdit={() => goToStep(2)}>
            <Row label="Overview">
              <p className="font-normal">{brief.description || '—'}</p>
            </Row>
            {brief.objectives.length ? (
              <Row label="Objectives">
                <span className="flex flex-wrap gap-1.5">
                  {brief.objectives.map((o) => (
                    <span
                      key={o}
                      className="rounded-full bg-[#E8F9D2] px-2.5 py-0.5 text-xs font-medium text-[#1F3D05]"
                    >
                      {o}
                    </span>
                  ))}
                </span>
              </Row>
            ) : null}
            <Row label="Target audience">
              <span className="font-normal">
                {audience || '—'}
                {brief.targetAudienceDescription ? (
                  <span className="block text-muted-foreground">
                    {brief.targetAudienceDescription}
                  </span>
                ) : null}
              </span>
            </Row>
            {brief.productName ? (
              <Row label="Product">
                <span className="flex items-center gap-3">
                  {brief.productPhotoUrl ? (
                    <img
                      src={brief.productPhotoUrl}
                      alt=""
                      className="h-10 w-10 rounded-md object-cover"
                    />
                  ) : null}
                  {brief.productName}
                </span>
              </Row>
            ) : null}
          </WizardCard>

          <WizardCard icon={ClipboardList} title="Content requirements" onEdit={() => goToStep(2)}>
            <Row label={basics.dealType === 'appearance' ? 'Appearance' : 'Content'}>
              {contentLine}
            </Row>
            {brief.creativeDirection ? (
              <Row label={basics.dealType === 'appearance' ? 'What happens' : 'Content guidance'}>
                <p className="font-normal">{brief.creativeDirection}</p>
              </Row>
            ) : null}
            {brief.primaryMessage || brief.cta ? (
              <Row label="Key message">
                <p className="font-normal">
                  {brief.primaryMessage}
                  {brief.cta ? (
                    <span className="block text-muted-foreground">CTA: {brief.cta}</span>
                  ) : null}
                </p>
              </Row>
            ) : null}
          </WizardCard>

          <WizardCard
            icon={Trophy}
            title={basics.dealType === 'gifting' ? 'Performance terms' : 'Pay'}
            onEdit={() => goToStep(1)}
          >
            {basics.dealType === 'gifting' ? (
              <>
                <Row label="Per athlete">
                  Product + {money(GIFTING_ACTIVATION_FEE)} Athios activation fee ·{' '}
                  {GIFTING_USAGE_DAYS} days paid usage
                </Row>
                <div>
                  <p className="text-xs text-muted-foreground">Performance bonuses (cumulative)</p>
                  <ul className="mt-1 divide-y divide-border">
                    {ladder.map((t) => (
                      <li key={t.views} className="flex justify-between py-1.5 text-sm">
                        <span className="text-foreground">{viewsLabel(t.views)} views</span>
                        <span className="font-semibold tabular-nums text-[#3F7A0A]">
                          +{money(t.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <Row label="Per athlete">{pay}</Row>
            )}
            <Row label="Who can join">
              {basics.openToApplications
                ? `Athletes can apply${basics.applicationDeadline ? ` until ${shortDate(basics.applicationDeadline)}` : ''}`
                : 'Invite only'}
            </Row>
          </WizardCard>
        </div>

        {/* How athletes will see it */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <section className="rounded-2xl border border-[#D9EFC2] bg-[#F6FDEE] p-4">
            <p className="flex items-center gap-2 font-semibold text-foreground">
              <Wand2 className="h-4 w-4 text-[#3F7A0A]" /> Athlete preview
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              This is how your campaign will appear to athletes.
            </p>
            <div className="mt-3 rounded-xl border border-border bg-card p-4 shadow-sm">
              {basics.logoUrl ? (
                <img src={basics.logoUrl} alt="" className="h-11 w-11 rounded-lg object-cover" />
              ) : (
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#0EA5E9] text-sm font-bold"
                  style={{ color: '#fff' }}
                >
                  {initials(basics.brand || '?')}
                </span>
              )}
              <p className="mt-3 font-bold text-foreground">{basics.name || 'Your campaign'}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span
                  className={cn(
                    'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    TONES[type.tone].chip,
                  )}
                >
                  {type.label}
                </span>
                {platforms.map((p) => {
                  const Icon = p.icon
                  return (
                    <span
                      key={p.id}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs"
                    >
                      <Icon className={cn('h-3 w-3', p.color)} /> {p.id}
                    </span>
                  )
                })}
              </div>
              {photo ? (
                <img
                  src={photo}
                  alt=""
                  className="mt-3 aspect-[4/3] w-full rounded-lg object-cover"
                />
              ) : null}
              {brief.description ? (
                <p className="mt-3 text-sm text-foreground">{brief.description}</p>
              ) : null}
              <ul className="mt-3 space-y-2 text-sm text-foreground">
                {dates ? (
                  <li className="flex gap-2">
                    <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" /> {dates}
                  </li>
                ) : null}
                {brief.productName ? (
                  <li className="flex gap-2">
                    <Gift className="h-4 w-4 shrink-0 text-muted-foreground" /> Product:{' '}
                    {brief.productName}
                  </li>
                ) : null}
                {audience ? (
                  <li className="flex gap-2">
                    <Users className="h-4 w-4 shrink-0 text-muted-foreground" /> {audience}
                  </li>
                ) : null}
                <li className="flex gap-2">
                  <Leaf className="h-4 w-4 shrink-0 text-muted-foreground" /> {contentLine}
                </li>
                <li className="flex gap-2">
                  <Trophy className="h-4 w-4 shrink-0 text-muted-foreground" /> {pay}
                </li>
              </ul>
              <button
                type="button"
                disabled
                className="mt-4 w-full rounded-lg bg-[#C0FD72] py-2.5 text-sm font-semibold text-black"
              >
                {basics.openToApplications ? 'Apply to this campaign' : 'View offer'}
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              A preview. The campaign page may include more details.
            </p>
          </section>

          <section className="rounded-2xl border border-[#BFD7FB] bg-[#EFF5FF] p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Info className="h-4 w-4 text-[#2563EB]" /> What happens next?
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {basics.openToApplications
                ? 'Your campaign goes live in the Marketplace and athletes can apply. You can also invite athletes, review applications and manage content from Campaigns.'
                : 'Your campaign is created as invite only. Shop athletes’ content in the Marketplace and book what fits; manage everything from Campaigns.'}
            </p>
          </section>
        </aside>
      </div>

      <WizardFooter
        onBack={() => goToStep(2)}
        primaryLabel="Create campaign"
        primaryIcon={<CheckCircle2 className="h-4 w-4" />}
        onPrimary={create}
        primaryBusy={isLoading}
        primaryDisabled={!basics.name || !basics.brand}
      />
    </div>
  )
}

export default Step3Review
